import React from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import imageResizer from 'js/image-resizer';
import ImageSource from 'components/models/image-source';

const DEFAULT_RESOLUTION = 50;
const SMALL_IMAGE_RESOLUTION = 10;

const transforms = {
  fill: 'fill',
  fit: 'fit',
};

const retinaMultipliers = [1, 2];

const buildResizedSet = (src, width, height, transform, focusPoint) => {
  if (!width && !height) return [];

  const resizeType =
    transform === transforms.fill
      ? imageResizer.Transforms.downFill
      : imageResizer.Transforms.downFit;
  const x = focusPoint?.x ?? 0;
  const y = focusPoint?.y ?? 0;

  const resolutionByWidth = (width ?? 0) > (height ?? 0);
  const resolutionSize = resolutionByWidth ? width : height;
  const resolution =
    resolutionSize > DEFAULT_RESOLUTION
      ? DEFAULT_RESOLUTION
      : SMALL_IMAGE_RESOLUTION;

  const widthResolution = resolutionByWidth ? resolution : 1;
  const heightResolution = resolutionByWidth ? 1 : resolution;

  return retinaMultipliers.map(multiplier => {
    let resizeWidth = width
      ? imageResizer.calcImageWidth(width, multiplier, widthResolution)
      : undefined;
    let resizeHeight = height
      ? imageResizer.calcImageHeight(height, multiplier, heightResolution)
      : undefined;

    if (resolutionByWidth && resizeHeight) {
      resizeHeight = Math.ceil(resizeWidth / (width / height));
    } else if (!resolutionByWidth && resizeWidth) {
      resizeWidth = Math.ceil(resizeHeight / (height / width));
    }

    return {
      url: imageResizer.buildResizeUrl(
        src,
        resizeWidth,
        resizeHeight,
        resizeType,
        x,
        y
      ),
      descriptor: `${multiplier}x`,
    };
  });
};

const Image = ({
  src,
  alt = '',
  className,
  sources = [],
  fluid = false,
  fetchPriority,
  loading,
  transform = transforms.fill,
  focusPoint,
}) => {
  if (!src) return null;

  return (
    <picture
      className={cn('image', className, {
        image_fluid: fluid,
      })}
    >
      {src && imageResizer.checkImageCanBeResized(src)
        ? [...sources]
            .sort((a, b) => b.media - a.media)
            .map(({ width, height, media, imageSrc }, index, arr) => {
              const resizedSet = buildResizedSet(
                imageSrc ?? src,
                width,
                height,
                transform,
                focusPoint
              );
              const srcSet = resizedSet
                .map(({ url, descriptor }) => `${url} ${descriptor}`)
                .join(', ');
              const isLast = index === arr.length - 1;

              return (
                <source
                  key={media || width}
                  {...(media &&
                    !isLast && { media: `(min-width: ${media}px)` })}
                  srcSet={srcSet}
                />
              );
            })
        : null}
      <img
        loading={loading}
        fetchPriority={fetchPriority}
        className="image__img"
        src={src}
        alt={alt}
      />
    </picture>
  );
};

Image.propTypes = {
  src: PropTypes.string.isRequired,
  alt: PropTypes.string,
  className: PropTypes.string,
  fluid: PropTypes.bool,
  sources: PropTypes.arrayOf(PropTypes.exact(ImageSource.propTypes)),
  focusPoint: PropTypes.shape({
    x: PropTypes.number.isRequired,
    y: PropTypes.number.isRequired,
  }),
  transform: PropTypes.oneOf(Object.values(transforms)),
  fetchPriority: PropTypes.oneOf(['high', 'low', 'auto']),
  loading: PropTypes.oneOf(['eager', 'lazy']),
};

Image.viewModelMeta = {
  sources: 'ignore',
  fetchPriority: 'ignore',
  loading: 'ignore',
  transform: 'ignore',
  className: 'ignore',
  fluid: 'ignore',
};

export default Image;
